import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableCellNode } from '@lexical/table';
import {
  $getSelection,
  $isParagraphNode,
  $nodesOfType,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useEffect, useRef } from 'react';
import {
  $focusSuggestionMark,
  $setMarkersHiddenOnMark,
  $sourceSuggestionMark,
  SuggestionMarkNode,
} from '../nodes/SuggestionMarkNode';
import {
  $focusSuggestion,
  $getSuggestionState,
  $isSuggestionNode,
  $setMarkersHidden,
  $setSuggestionFullCell,
  $sourceSuggestion,
  SuggestionNode,
} from '../nodes/SuggestionNode';
import { SUGGESTION_COLORS } from '../nodes/suggestion-colors';
import { Source } from '../types';
import { $containingSuggestionOrMark } from '../utils';

type Props = {
  showMarkers?: boolean;
  hiddenSources?: Source[];
};

export function SuggestionPlugin({ showMarkers = true, hiddenSources }: Props) {
  const [editor] = useLexicalComposerContext();
  useSuggestionCellStyle(editor);
  useSuggestionFocusStyle(editor, showMarkers);
  useMarkersVisibility(editor, showMarkers, hiddenSources);
  return null;
}

function useSuggestionCellStyle(editor: LexicalEditor) {
  useEffect(() => {
    const unregisterTransform = editor.registerNodeTransform(SuggestionNode, node => {
      const parent = node.getParent();
      const grandParent = parent?.getParent();

      const parentIsParagraph = $isParagraphNode(parent);
      const parentIsTableCell = $isTableCellNode(parent);
      const grandParentIsTableCell = $isTableCellNode(grandParent);

      const isInTableCell = (parentIsParagraph && grandParentIsTableCell) || parentIsTableCell;

      const containerChildren = parent?.getChildren() || [];

      const isOnlyContent = containerChildren.every(
        child => child === node || child.getTextContent().trim().length === 0,
      );

      const suggestionIsOnlyCellContent = isInTableCell && isOnlyContent;

      const currentState = $getSuggestionState(node);

      if (currentState.isFullCell !== suggestionIsOnlyCellContent) {
        $setSuggestionFullCell(node, suggestionIsOnlyCellContent);
      }
    });

    const unregisterUpdate = editor.registerUpdateListener(() => {
      const rootElement = editor.getRootElement();
      if (!rootElement) return;

      rootElement.querySelectorAll<HTMLElement>('[data-suggestion-cell]').forEach(cell => {
        cell.style.backgroundColor = '';
        cell.style.transition = '';
        delete cell.dataset.suggestionCell;
      });

      editor.getEditorState().read(() => {
        for (const node of $nodesOfType(SuggestionNode)) {
          const state = $getSuggestionState(node);
          if (!state.isFullCell || state.markersHidden) continue;

          const element = editor.getElementByKey(node.getKey());
          if (!element) continue;

          const cell = element.closest<HTMLElement>(
            '.editor-table-cell, .editor-table-cell-header',
          );
          if (!cell) continue;

          const bgColor =
            SUGGESTION_COLORS[state.source][state.focusedAt != null ? 'focused' : 'unfocused'];
          cell.style.backgroundColor = bgColor;
          cell.style.transition = 'background-color 0.4s ease-in-out';
          cell.dataset.suggestionCell = 'true';
        }
      });
    });

    return () => {
      unregisterTransform();
      unregisterUpdate();
    };
  }, [editor]);
}

function useSuggestionFocusStyle(editor: LexicalEditor, showMarkers: boolean) {
  const showMarkersRef = useRef(showMarkers);

  useEffect(() => {
    showMarkersRef.current = showMarkers;
  }, [showMarkers]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        if (!showMarkersRef.current) return false;

        const selection = $getSelection();
        selection?.getNodes().forEach(node => {
          const match = $containingSuggestionOrMark(node);
          if (!match) return;
          if ($isSuggestionNode(match)) {
            $focusSuggestion(match);
          } else {
            $focusSuggestionMark(match);
          }
        });
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);
}

function useMarkersVisibility(
  editor: LexicalEditor,
  showMarkers: boolean,
  hiddenSources?: Source[],
) {
  const prevShowMarkersRef = useRef(showMarkers);
  const latestRef = useRef({ showMarkers, hiddenSources });
  latestRef.current = { showMarkers, hiddenSources };

  useEffect(() => {
    if (prevShowMarkersRef.current !== showMarkers) {
      prevShowMarkersRef.current = showMarkers;
      editor.update(() => {
        $setSelection(null);
      });
    }

    editor.update(() => {
      $nodesOfType(SuggestionNode).forEach(node => {
        const source = $sourceSuggestion(node);
        const hidden = !showMarkers || (hiddenSources?.includes(source) ?? false);
        $setMarkersHidden(node, hidden);
      });
      $nodesOfType(SuggestionMarkNode).forEach(node => {
        const source = $sourceSuggestionMark(node);
        const hidden = !showMarkers || (hiddenSources?.includes(source) ?? false);
        $setMarkersHiddenOnMark(node, hidden);
      });
    });
  }, [editor, showMarkers, hiddenSources]);

  useEffect(() => {
    const unregisterSuggestion = editor.registerNodeTransform(SuggestionNode, node => {
      const { showMarkers: show, hiddenSources: hidden } = latestRef.current;
      const source = $sourceSuggestion(node);
      const shouldHide = !show || (hidden?.includes(source) ?? false);
      if ($getSuggestionState(node).markersHidden !== shouldHide) {
        $setMarkersHidden(node, shouldHide);
      }
    });

    const unregisterMark = editor.registerNodeTransform(SuggestionMarkNode, node => {
      const { showMarkers: show, hiddenSources: hidden } = latestRef.current;
      const source = $sourceSuggestionMark(node);
      const shouldHide = !show || (hidden?.includes(source) ?? false);
      $setMarkersHiddenOnMark(node, shouldHide);
    });

    return () => {
      unregisterSuggestion();
      unregisterMark();
    };
  }, [editor]);
}
